import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { Search, X, Pill, BookOpen, Stethoscope, GraduationCap, Loader2 } from "lucide-react";

type SearchResultType = "condition" | "medication" | "guideline" | "scenario";

interface SearchResultItem {
  type: SearchResultType;
  id: number;
  title: string;
  subtitle: string | null;
}

const TYPE_CONFIG: Record<SearchResultType, { label: string; icon: React.ElementType; badge: string }> = {
  condition: { label: "Condition", icon: Stethoscope, badge: "bg-teal-50 text-teal-700 border-teal-100" },
  medication: { label: "Medication", icon: Pill, badge: "bg-blue-50 text-blue-700 border-blue-100" },
  guideline: { label: "Guideline", icon: BookOpen, badge: "bg-purple-50 text-purple-700 border-purple-100" },
  scenario: { label: "Scenario", icon: GraduationCap, badge: "bg-amber-50 text-amber-700 border-amber-100" },
};

function getDestinationUrl(item: SearchResultItem, role: string): string {
  if (role === "patient") {
    if (item.type === "condition") return `/patient/conditions/${item.id}`;
    if (item.type === "medication") return `/patient/know-your-medication/${item.id}`;
    return "/patient";
  }
  if (item.type === "condition") return `/nurse/conditions/${item.id}`;
  if (item.type === "medication") return `/nurse/pharmacology/${item.id}`;
  if (item.type === "guideline") return `/nurse/guidelines/${item.id}`;
  if (item.type === "scenario") return `/nurse/scenarios/${item.id}`;
  return "/";
}

export function GlobalSearch() {
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const role = localStorage.getItem("rheumhub_role") ?? "nurse";

  const doSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setIsLoading(true);
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q.trim())}`, {
        signal: abortRef.current.signal,
      });
      if (!res.ok) {
        setResults([]);
        return;
      }
      const data: SearchResultItem[] = await res.json();
      setResults(data.slice(0, 8));
      setActiveIndex(-1);
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== "AbortError") {
        setResults([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    debounceRef.current = setTimeout(() => {
      void doSearch(query);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navigate = useCallback((item: SearchResultItem) => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    setLocation(getDestinationUrl(item, role));
  }, [role, setLocation]);

  const goToAllResults = useCallback(() => {
    const q = query.trim();
    setIsOpen(false);
    setQuery("");
    setResults([]);
    setLocation(`/search?q=${encodeURIComponent(q)}`);
  }, [query, setLocation]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setQuery("");
      setResults([]);
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        navigate(results[activeIndex]);
      } else if (query.trim().length >= 2) {
        goToAllResults();
      }
    }
  };

  const showDropdown = isOpen && query.trim().length >= 2;

  return (
    <div ref={containerRef} className="relative">
      <div className={`flex items-center gap-2 bg-slate-50 border rounded-lg px-3 py-1.5 transition-all ${
        isOpen ? "border-teal-400 ring-1 ring-teal-300 bg-white" : "border-slate-200 hover:border-slate-300"
      }`}>
        {isLoading ? (
          <Loader2 className="h-4 w-4 text-slate-400 shrink-0 animate-spin" aria-hidden />
        ) : (
          <Search className="h-4 w-4 text-slate-400 shrink-0" aria-hidden />
        )}
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search conditions, drugs, guidelines…"
          className="w-52 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
          aria-label="Global search"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? "global-search-listbox" : undefined}
          aria-activedescendant={activeIndex >= 0 ? `search-option-${activeIndex}` : undefined}
          autoComplete="off"
          spellCheck={false}
          data-testid="global-search-input"
          role="combobox"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Clear search"
            type="button"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
        {!query && (
          <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] text-slate-400 bg-slate-100 border border-slate-200 rounded px-1 py-0.5 font-mono leading-none select-none" aria-label="Keyboard shortcut: Command K">
            <span aria-hidden>⌘</span><span>K</span>
          </kbd>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute right-0 top-full mt-1.5 w-80 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden z-50"
          data-testid="global-search-dropdown"
        >
          {results.length === 0 && !isLoading && (
            <p className="px-4 py-6 text-center text-sm text-slate-500">
              No results for <span className="font-medium">"{query}"</span>
            </p>
          )}

          {results.length > 0 && (
            <div
              id="global-search-listbox"
              role="listbox"
              aria-label="Search results"
              className="py-1 max-h-80 overflow-y-auto"
            >
              {results.map((item, idx) => {
                const cfg = TYPE_CONFIG[item.type];
                const Icon = cfg.icon;
                const isActive = idx === activeIndex;
                return (
                  <div key={`${item.type}-${item.id}`} role="option" aria-selected={isActive} id={`search-option-${idx}`}>
                    <button
                      type="button"
                      onClick={() => navigate(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors text-left ${
                        isActive ? "bg-slate-50" : "hover:bg-slate-50"
                      }`}
                      tabIndex={-1}
                    >
                      <span className={`shrink-0 p-1.5 rounded-md border ${cfg.badge}`} aria-hidden>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-slate-800 truncate">{item.title}</span>
                        {item.subtitle && (
                          <span className="block text-xs text-slate-500 truncate mt-0.5">{item.subtitle}</span>
                        )}
                      </span>
                      <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded border ${cfg.badge}`} aria-hidden>
                        {cfg.label}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between">
            <p className="text-xs text-slate-400">
              {results.length > 0 ? `${results.length} result${results.length !== 1 ? "s" : ""}` : ""}
            </p>
            <button
              type="button"
              onClick={goToAllResults}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium"
              data-testid="global-search-see-all"
            >
              See all results →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
